/**
 * Toast notes about curves a motion command had to split (W1-10b core-commands-motion; DESIGN.md §6.4, §5.4).
 *
 * A shape-preserving split (splitKey.ts `insertKeyRec`) is exact for most curves; when it is not (an approximate
 * ease split, a baked half, a rounded colour, a type-on / counter text, a changed lead-in, a GIF that played on its
 * own) the command says so in one toast, with the deviation: a percentage of the curve's range, or for text how
 * many milliseconds a character appears earlier or later (a text split is approximate by its class, D§6.4, even
 * where the measured shift is 0: the toast then says the characters still appear at the same moments). Commands
 * that split several tracks (insertKeysAt, paste, reverse, remove time, move to layer) collect the reports with
 * `collectSplit` and note only the track whose curve changed the most; splits made on the other axis of an
 * X/Y-linked line are reported with their own note (`notePartnerSplit`).
 */

import type { TxBuilder } from '../ops/types';
import { MOTION_MSG, reason, type TrackInfo } from './motionTrack';
import { worseSplit, type SplitReport } from './splitKey';
import { shiftMs } from './textDisplay';

/** A split report and the track it happened on. */
export interface SplitOn<R extends SplitReport = SplitReport> {
  readonly info: TrackInfo;
  readonly res: R;
}

/** Percent shown for a relative deviation (rounded up to 0.01 %, at least 0.01 %). */
export function percentOf(rel: number): number {
  return Math.max(0.01, Math.ceil(rel * 10000) / 100);
}

/** The split that changed its curve the most (worse exactness class first, then the larger deviation). */
export function worseSplitOn<R extends SplitReport>(a: SplitOn<R> | null, b: SplitOn<R> | null): SplitOn<R> | null {
  if (a === null) return b;
  if (b === null) return a;
  return worseSplit(a.res, b.res) === a.res ? a : b;
}

/** Adds one track's report to a collection (exact reports are dropped). */
export function collectSplit(acc: SplitOn | null, info: TrackInfo, res: SplitReport): SplitOn | null {
  if (res.exactness === 'exact') return acc;
  return worseSplitOn(acc, { info, res });
}

/** The toast notes of a split that changed the curve (none when exact). */
export function noteSplit(tx: TxBuilder, info: TrackInfo, res: SplitReport): void {
  if (res.exactness === 'exact') return;
  if (res.leadInRelError !== undefined) {
    tx.note(reason(MOTION_MSG.splitLeadIn, { percent: percentOf(res.leadInRelError) }));
  }
  // Text: how far a character-change time moved, in whole milliseconds (0: the characters appear at the same
  // moments). Only a measured display gets the text notes; a text key set to a new value has no display to compare
  // with, and its approximation is the curve's (an Expo or Elastic ease split).
  const measuredText = info.valueType === 'string' && res.textShiftSec !== undefined;
  const ms = shiftMs(res.textShiftSec ?? 0);
  if (res.exactness === 'bake') {
    tx.note(
      measuredText
        ? reason(MOTION_MSG.splitBakedText, { ms })
        : reason(MOTION_MSG.splitBaked, { percent: percentOf(res.maxRelError) }),
    );
    return;
  }
  switch (res.approx) {
    case 'leadIn':
      tx.note(reason(MOTION_MSG.splitLeadIn, { percent: percentOf(res.maxRelError) }));
      return;
    case 'gif':
      tx.note(reason(MOTION_MSG.splitApproxGif));
      return;
    case 'linearHalf':
      tx.note(reason(MOTION_MSG.splitLinearHalf));
      return;
    case 'leadInShift':
      tx.note(reason(MOTION_MSG.timeLeadIn, { percent: percentOf(res.maxRelError) }));
      return;
    case undefined:
      break;
  }
  if (measuredText) tx.note(reason(MOTION_MSG.splitApproxText, { ms }));
  else if (info.valueType === 'rgb' || info.valueType === 'rgba8') tx.note(reason(MOTION_MSG.splitApproxColor));
  else tx.note(reason(MOTION_MSG.splitApprox, { percent: percentOf(res.maxRelError) }));
}

/** Notes the worst split of a collection (nothing when every split was exact). */
export function noteWorstSplit(tx: TxBuilder, acc: SplitOn | null): void {
  if (acc !== null) noteSplit(tx, acc.info, acc.res);
}

/** The toast note of keys mirrored onto the other position axis that changed its curve (none when exact). */
export function notePartnerSplit(tx: TxBuilder, res: SplitReport | null | undefined): void {
  if (res === null || res === undefined || res.exactness === 'exact') return;
  const kind =
    res.exactness === 'bake' ? 'bake' : res.approx === 'leadIn' || res.approx === 'leadInShift' ? 'leadIn' : 'approx';
  tx.note(reason(MOTION_MSG.splitPartner, { kind, percent: percentOf(res.maxRelError) }));
  if (res.leadInRelError !== undefined) {
    tx.note(reason(MOTION_MSG.splitPartner, { kind: 'leadIn', percent: percentOf(res.leadInRelError) }));
  }
}
